import axios from "axios";
import { isAbortError } from "../utils/cache";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api/v1`,
  withCredentials: true,
});

// The csrf_token cookie is set by the backend's domain, which differs from
// this frontend's origin in production, so document.cookie can't read it.
// Instead we cache the value the backend hands back in response bodies.
let csrfToken = "";
export function setCsrfToken(token) {
  csrfToken = token || "";
}

export async function primeCsrfToken() {
  try {
    const res = await api.get("/auth/csrf");
    setCsrfToken(res.data?.csrf_token);
  } catch {
    // no active session yet — nothing to prime
  }
}

api.interceptors.request.use((config) => {
  if (csrfToken) config.headers["X-CSRF-Token"] = csrfToken;

  const token = localStorage.getItem("token");
  if (token && !config.headers["Authorization"]) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  return config;
});

let _refreshing = false;
let _pending = [];

// /auth/me is the session-restore probe — a 401 just means no active session,
// never attempt a refresh for it (that would cause a redirect loop on first load).
const _SKIP_REFRESH = ["/auth/refresh/web", "/auth/login", "/auth/login/web", "/auth/me"];

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    const isSkip = _SKIP_REFRESH.some((p) => original?.url?.includes(p));

    if (err.response?.status === 401 && !original?._retry && !isSkip) {
      // The failing request's config already has a (now-stale) Authorization
      // header baked in from its first dispatch — the request interceptor
      // only ever adds that header when one isn't already present, so simply
      // retrying `original` as-is would resend the same expired bearer token
      // forever, never picking up the fresh cookie/token the refresh below
      // just issued. Clearing it lets the retry fall back to the refreshed
      // cookie, or pick up the refreshed localStorage token via the interceptor.
      const retryWithFreshAuth = (config) => {
        if (config?.headers) delete config.headers["Authorization"];
        return api(config);
      };

      if (_refreshing) {
        return new Promise((resolve, reject) => _pending.push({ resolve, reject })).then(
          () => retryWithFreshAuth(original)
        );
      }

      original._retry = true;
      _refreshing = true;

      try {
        const refreshRes = await api.post("/auth/refresh/web");
        setCsrfToken(refreshRes.data?.csrf_token);
        if (refreshRes.data?.access_token) {
          localStorage.setItem("token", refreshRes.data.access_token);
        }
        _pending.forEach((p) => p.resolve());
        _pending = [];
        return retryWithFreshAuth(original);
      } catch (refreshErr) {
        _pending.forEach((p) => p.reject(refreshErr));
        _pending = [];
        localStorage.removeItem("user");
        localStorage.removeItem("role");
        localStorage.removeItem("token");
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(refreshErr);
      } finally {
        _refreshing = false;
      }
    }

    if (err.response?.status === 429 && !err.rateLimitAware) {
      const retryAfter = err.response.headers?.["retry-after"];
      const retryHint = retryAfter ? ` Please retry in ${retryAfter} seconds.` : "";
      err.rateLimitAware = true;
      err.userMessage = `Too many requests were sent from the website.${retryHint}`;
    }

    // No response at all — the request never reached a server.
    // This can be: a DNS failure, dropped connection, CORS/CSP block (all
    // appear as err.code === "ERR_NETWORK"), or a client-side timeout
    // (err.code === "ECONNABORTED"). Give specific, actionable messages.
    if (!err.response && !isAbortError(err)) {
      err.isNetworkError = true;
      const apiUrl  = process.env.REACT_APP_API_URL || "";

      const isLocal = apiUrl.includes("localhost") || apiUrl.includes("127.0.0.1");

      if (err.code === "ECONNABORTED") {
        err.userMessage =
          "Request timed out \u2014 the server is not responding. " +
          "It may be starting up. Please wait 30 seconds and try again.";
      } else if (isLocal) {
        // Local dev backend is stopped
        const serverAddr = apiUrl.replace(/https?:\/\//, "");
        err.userMessage =
          `Server is not running (${serverAddr}). ` +
          "Please start the local backend and try again.";
      } else if (apiUrl) {
        // Configured remote API is unreachable
        err.userMessage =
          "Cannot reach the server. The backend may be offline or still starting up. " +
          "Please wait a moment and try again.";
      } else {
        err.userMessage =
          "No internet connection. Please check your network and try again.";
      }
    }

    return Promise.reject(err);
  }
);

export default api;
